import React, { useState, useEffect } from 'react';
import {
  SparklesIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';
import PaymentModal from '../components/PaymentModal';

const SubscriptionPage = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isGhana, setIsGhana] = useState(false);

  // Single fetchSubscriptionData function
  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);

      // Always fetch subscription data regardless of location detection
      const [statusResponse, packagesResponse] = await Promise.all([
        api.getSubscriptionStatus(),
        api.getPackagesWithPricing()
      ]);

      setSubscriptionStatus(statusResponse.data.widget);
      setPackages(packagesResponse.data.packages || []);

      // Try to detect location, but don't let failure affect the main functionality
      try {
        const locationResponse = await fetch('https://ipapi.co/json/', {
          timeout: 5000 // 5 second timeout
        });
        const locationData = await locationResponse.json();
        if (locationData.country_code === 'GH') {
          setIsGhana(true);
        }
      } catch (locationError) {
        console.log('Location detection failed, defaulting to USD pricing:', locationError);
        // Keep isGhana as false (default USD pricing)
      }

    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const formatPrice = (price) => {
    if (isGhana) {
      return `₵${Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleUpgrade = (pkg) => {
    setSelectedPackage(pkg);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setSelectedPackage(null);
    fetchSubscriptionData(); // Refresh subscription status
    toast.success('Subscription upgraded successfully!');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'trial':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
            <ClockIcon className="w-4 h-4 mr-1" />
            Free Trial
          </span>
        );
      case 'active':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckIcon className="w-4 h-4 mr-1" />
            Active
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
            Expired
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
          <p className="text-gray-600 mt-1">Manage your subscription and upgrade your plan</p>
        </div>
        <button
          onClick={fetchSubscriptionData}
          className="btn-secondary btn-sm"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Current Subscription Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Current Subscription</h2>
        </div>
        <div className="p-6">
          {subscriptionStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Status */}
              <div className="text-center">
                <div className="text-sm font-medium text-gray-500 mb-2">Status</div>
                {getStatusBadge(subscriptionStatus.subscription?.status)}
              </div>

              {/* Package */}
              <div className="text-center">
                <div className="text-sm font-medium text-gray-500 mb-2">Current Plan</div>
                <div className="text-lg font-semibold text-gray-900">
                  {subscriptionStatus.subscription?.package_name || 'No Package'}
                </div>
              </div>

              {/* Usage */}
              <div className="text-center">
                <div className="text-sm font-medium text-gray-500 mb-2">Usage This Month</div>
                <div className="text-lg font-semibold text-gray-900">
                  {subscriptionStatus.usage?.usage_percentage?.toFixed(1) || 0}%
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {subscriptionStatus.usage?.characters_used?.toLocaleString() || 0} / {' '}
                  {subscriptionStatus.usage?.characters_limit === 'unlimited'
                    ? 'Unlimited'
                    : parseInt(subscriptionStatus.usage?.characters_limit || 0).toLocaleString()
                  } characters
                </div>
              </div>

              {/* Trial Info */}
              {subscriptionStatus.trial && subscriptionStatus.trial.is_trial && (
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-500 mb-2">Trial Expires</div>
                  <div className="text-lg font-semibold text-orange-600">
                    {subscriptionStatus.trial.days_remaining} days
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {/*{new Date(subscriptionStatus.trial.expires_at).toLocaleDateString()}*/}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Unable to load subscription status</p>
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {subscriptionStatus?.alerts && subscriptionStatus.alerts.length > 0 && (
        <div className="space-y-4">
          {subscriptionStatus.alerts.map((alert, index) => (
            <div
              key={index}
              className={`rounded-lg border p-4 ${
                alert.type === 'critical'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-yellow-50 border-yellow-200 text-yellow-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  <div>
                    <div className="font-semibold">{alert.message}</div>
                    <div className="text-sm mt-1">{alert.action}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Available Packages */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Available Plans</h2>
          <p className="text-sm text-gray-600 mt-1">Choose the perfect plan for your business needs</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg) => {
              const isCurrent = pkg.id === subscriptionStatus?.subscription?.package_id;
              const isRecommended = pkg.is_recommended;

              return (
                <div
                  key={pkg.id}
                  className={`relative rounded-xl border-2 p-6 transition-all hover:shadow-lg ${
                    isCurrent
                      ? 'border-green-500 bg-green-50'
                      : isRecommended
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  {/* Package Header */}
                  <div className="text-center mb-6">
                    {isRecommended && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          Most Popular
                        </span>
                      </div>
                    )}
                    {isCurrent && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          Current Plan
                        </span>
                      </div>
                    )}

                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      isCurrent ? 'bg-green-500' : isRecommended ? 'bg-blue-500' : 'bg-gray-500'
                    }`}>
                      <SparklesIcon className="h-6 w-6 text-white" />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{pkg.description}</p>

                    <div className="mt-4">
                      <div className="text-3xl font-bold text-gray-900">
                        {isGhana && pkg.pricing.local
                          ? formatPrice(pkg.pricing.local)
                          : formatPrice(pkg.pricing.usd)
                        }
                      </div>
                      <div className="text-sm text-gray-500">
                        per {pkg.pricing.billing_cycle}
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {pkg.features?.limits && Object.entries(pkg.features.limits).map(([key, value], featureIndex) => {
                      // Format the key to be more readable
                      const formatKey = (key) => {
                        return key.replace(/_/g, ' ')
                          .replace(/\b\w/g, l => l.toUpperCase());
                      };

                      // Format the value to be more readable
                      const formatValue = (value) => {
                        if (value === 'true') return '✓';
                        if (value === 'false') return '✗';
                        if (key === 'monthly_characters') return parseInt(value).toLocaleString() + ' characters';
                        if (key === 'monthly_messages') return parseInt(value).toLocaleString() + ' messages';
                        if (key === 'analytics_retention_days') return value + ' days';
                        return value;
                      };

                      return (
                        <div key={featureIndex} className="flex items-center justify-between text-sm text-gray-700">
                          <span>{formatKey(key)}</span>
                          <span className="font-medium">{formatValue(value)}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Action Button */}
                  <div className="text-center">
                    {isCurrent ? (
                      <div className="flex items-center justify-center py-2 text-green-600 font-semibold">
                        <CheckIcon className="h-5 w-5 mr-2" />
                        Current Plan
                      </div>
                    ) : (
                      <button
                        onClick={() => handleUpgrade(pkg)}
                        className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                          isRecommended
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-800 hover:bg-gray-900 text-white'
                        }`}
                      >
                        {pkg.cta_text || 'Upgrade to this plan'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Trial Extension (if on trial) */}
      {subscriptionStatus?.subscription?.status === 'trial' && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-orange-900">
                  Need More Time?
                </h3>
                <p className="text-sm text-orange-700">
                  Contact our support team if you need additional trial time to evaluate our service.
                </p>
              </div>
            </div>
            <a
             href="mailto:marketing@npontu.com"
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Request Extension
            </a>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        selectedPackage={selectedPackage}
        onPaymentSuccess={handlePaymentSuccess}
        isGhana={isGhana}
      />
    </div>
  );
};

export default SubscriptionPage;